import { EventEmitter } from 'events';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'notice';
  title: string;
  message: string;
  duration?: number;
  timestamp: Date;
  data?: any;
}

class NotificationService extends EventEmitter {
  private notifications: Notification[] = [];
  private maxNotifications = 10;
  private showSystemNotifications = false; // Control system notifications
  private publicLoginSentTime: number | null = null; // Track when we sent PublicLogin
  private publicLoginCooldown = 2000; // 2 seconds cooldown to ignore our own PublicLogin responses

  // Add a new notification
  addNotification(notification: Omit<Notification, 'id' | 'timestamp'>): string {
    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      ...notification,
      id,
      timestamp: new Date(),
      duration: notification.duration || this.getDefaultDuration(notification.type)
    };

    this.notifications.unshift(newNotification);
    
    // Keep only the latest notifications
    if (this.notifications.length > this.maxNotifications) {
      this.notifications = this.notifications.slice(0, this.maxNotifications);
    }

    this.emit('notificationAdded', newNotification);
    return id;
  }

  // Remove a notification
  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.emit('notificationRemoved', id);
  }

  // Clear all notifications
  clearAllNotifications(): void {
    this.notifications = [];
    this.emit('notificationsCleared');
  }

  // Get all notifications
  getNotifications(): Notification[] {
    return [...this.notifications];
  }

  // Get notifications by type
  getNotificationsByType(type: Notification['type']): Notification[] {
    return this.notifications.filter(n => n.type === type);
  }

  // Get unread notifications count
  getUnreadCount(): number {
    return this.notifications.length;
  }

  // Helper methods for different notification types
  success(title: string, message: string, duration?: number): string {
    return this.addNotification({ type: 'success', title, message, duration });
  }

  error(title: string, message: string, duration?: number): string {
    return this.addNotification({ type: 'error', title, message, duration });
  }

  warning(title: string, message: string, duration?: number): string {
    return this.addNotification({ type: 'warning', title, message, duration });
  }

  info(title: string, message: string, duration?: number): string {
    return this.addNotification({ type: 'info', title, message, duration });
  }

  notice(title: string, message: string, duration?: number): string {
    return this.addNotification({ type: 'notice', title, message, duration });
  }

  // Get default duration based on type
  private getDefaultDuration(type: Notification['type']): number {
    switch (type) {
      case 'success':
        return 5000; // 5 seconds
      case 'error':
        return 8000; // 8 seconds
      case 'warning':
        return 6000; // 6 seconds
      case 'info':
        return 4000; // 4 seconds
      case 'notice':
        return 7000; // 7 seconds
      default:
        return 5000;
    }
  }

  // Handle socket notifications
  handleSocketNotification(messageType: string, data: any): void {
    // Always skip system notifications - they're internal and shouldn't be shown to users
    if (this.isSystemNotification(messageType)) {
      return;
    }

    switch (messageType) {
      case 'notice':
        this.handleNoticeNotification(data);
        break;
      case 'noticeList':
        this.handleNoticeListNotification(data);
        break;
      case 'questionList':
        this.handleQuestionListNotification(data);
        break;
      case 'ForceLogout':
        this.handleForceLogoutNotification(data);
        break;
      case 'AutoLogoutWarning':
        this.handleAutoLogoutWarningNotification(data);
        break;
      case 'repair_site':
      case 'RepairSite':
        this.handleRepairSiteNotification(data);
        break;
      case 'PublicLogin':
        this.handlePublicLoginNotification(data);
        break;
      case 'DuplicateSocketWarning':
        this.handleDuplicateSocketWarningNotification(data);
        break;
      case 'userInfo':
        this.handleUserInfoNotification(data);
        break;
      case 'Connect':
        this.handleConnectNotification(data);
        break;
      case 'market_data':
        this.handleMarketDataNotification(data);
        break;
      case 'game_list':
        this.handleGameListNotification(data);
        break;
      case 'live_status':
        this.handleLiveStatusNotification(data);
        break;
      case 'powerball_status':
        this.handlePowerballStatusNotification(data);
        break;
      case 'price_rate':
        this.handlePriceRateNotification(data);
        break;
      case 'gameCnt':
      case 'gameCntArr':
        this.handleGameCntNotification(data);
        break;
      default:
        // Don't show notifications for system/internal messages
        // Only show for user-facing notifications with meaningful content
        if (!this.isSystemNotification(messageType)) {
          // Only show if it's a user-facing notification with actual message content
          if (data.message && typeof data.message === 'string' && data.message.trim()) {
            this.info('System Update', data.message, 3000);
          }
        }
        // Silently ignore system notifications
    }
  }

  private handleNoticeNotification(data: any): void {
    if (data.bet && Array.isArray(data.bet)) {
      data.bet.forEach((message: string) => {
        this.notice('Bet Notification', message);
      });
    }
    if (data.deposit && Array.isArray(data.deposit)) {
      data.deposit.forEach((message: string) => {
        this.info('Deposit Notification', message);
      });
    }
    if (data.withdraw && Array.isArray(data.withdraw)) {
      data.withdraw.forEach((message: string) => {
        this.info('Withdraw Notification', message);
      });
    }
    if (data.general && Array.isArray(data.general)) {
      data.general.forEach((message: string) => {
        this.info('General Notification', message);
      });
    }
  }

  private handleNoticeListNotification(data: any): void {
    if (data.status) {
      this.info('Notice Update', 'New notices are available', 4000);
    }
  }

  private handleQuestionListNotification(data: any): void {
    if (data.status) {
      this.info('Question Update', 'New questions are available', 4000);
    }
  }

  private handleForceLogoutNotification(data: any): void {
    this.error('Force Logout', 'You have been logged out by the system', 10000);
  }

  private handleAutoLogoutWarningNotification(data: any): void {
    this.warning('Auto Logout Warning', 'You will be automatically logged out soon', 8000);
  }

  private handleRepairSiteNotification(data: any): void {
    // Only show maintenance notification if site is actually in maintenance mode
    // Check if maintenance state is active (state !== 0 means maintenance is active)
    if (data.state !== undefined && data.state !== 0) {
      this.warning('Site Maintenance', 'The site is under maintenance', 10000);
    }
    // If state is 0 or undefined, maintenance has ended or is not active, don't show notification
  }

  private handlePublicLoginNotification(data: any): void {
    // Only show notification if it's actually a login from another device
    // Don't show if:
    // 1. It's a response to our own PublicLogin message (within cooldown period)
    // 2. It's just a status confirmation (status: 0 or 1)
    // 3. It doesn't indicate a new login from another device
    
    const now = Date.now();
    const isOurOwnMessage = this.publicLoginSentTime && (now - this.publicLoginSentTime) < this.publicLoginCooldown;
    
    // If it's a response to our own PublicLogin (status confirmation), don't show
    if (isOurOwnMessage) {
      return;
    }
    
    // If it's just a status response without indicating another device login, don't show
    if (data.status !== undefined && !data.message && !data.deviceInfo) {
      return;
    }
    
    // Only show if there's an indication it's from another device
    // Check if user is currently logged in (to avoid showing on initial connection)
    const isLoggedIn = !!localStorage.getItem('authToken');
    if (!isLoggedIn) {
      // Don't show if user is not logged in (might be initial connection)
      return;
    }
    
    // Show notification only if it's a genuine login from another device
    this.info('Login Notification', 'You have logged in from another device', 6000);
  }
  
  // Method to track when we send PublicLogin (called from websocket service)
  public markPublicLoginSent(): void {
    this.publicLoginSentTime = Date.now();
  }

  private handleDuplicateSocketWarningNotification(data: any): void {
    this.warning('Connection Warning', 'Multiple connections detected', 6000);
  }

  private handleUserInfoNotification(data: any): void {
    this.info('Account Update', 'Your account information has been updated', 4000);
  }

  private handleConnectNotification(data: any): void {
    // Connect notifications are usually internal system messages
    // Only show them if they contain important information
    if (data.message || data.status) {
      this.info('Connection Update', data.message || 'Connection status updated', 3000);
    }
    // For silent connect messages, don't show notification
  }

  private handleMarketDataNotification(data: any): void {
    // Market data updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Market Update', 'Market data has been updated', 3000);
    }
  }

  private handleGameListNotification(data: any): void {
    // Game list updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Game Update', 'Game list has been updated', 3000);
    }
  }

  private handleLiveStatusNotification(data: any): void {
    // Live status updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Live Update', 'Live status has been updated', 3000);
    }
  }

  private handlePowerballStatusNotification(data: any): void {
    // Powerball status updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Powerball Update', 'Powerball status has been updated', 3000);
    }
  }

  private handlePriceRateNotification(data: any): void {
    // Price rate updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Price Update', 'Price rates have been updated', 3000);
    }
  }

  private handleGameCntNotification(data: any): void {
    // Game count updates are usually silent background updates
    // Only show if there's an important status change
    if (data.status && data.status !== 'success') {
      this.info('Game Count Update', 'Game count has been updated', 3000);
    }
  }

  private isSystemNotification(messageType: string): boolean {
    const systemTypes = [
      'Connect',
      'market_data',
      'game_list',
      'live_status',
      'powerball_status',
      'price_rate',
      'gameCnt',
      'gameCntArr',
      'Sport_Data',
      'market_setting',
      'bet_setting',
      'game_setting',
      'baseline_setting',
      'provider_setting',
      'reconnect_setting',
      'delay_setting',
      'betgame_status',
      'GameList',
      'ScoreData',
      'SportsList',
      'EventCount',
      'BetSetting',
      'MarketSetting',
      'BaselineSetting',
      'ProviderSetting',
      'ReconnectSetting',
      'DelaySetting'
    ];
    return systemTypes.includes(messageType);
  }

  // Configuration methods
  setSystemNotificationsEnabled(enabled: boolean): void {
    this.showSystemNotifications = enabled;
  }

  isSystemNotificationsEnabled(): boolean {
    return this.showSystemNotifications;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
